// Billing → Current Payment Collections.
//
// The collections the team is actively chasing this month, curated by hand:
// nothing appears here on its own, cases are added with the Add Case button.
// That list is its own thing — separate from the Current Client List, so adding
// a case to one does not put it on the other (see the two override tables).
//
// Membership is per CASE, but the rows are BILLS: adding a case brings all of
// that case's bills for the month onto the page, and removing takes them all
// away again.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { clientLabel, collectedOf, effectiveStatus, isCollected, outstandingOf } from '../utils/billStatus';
import { compareByDutyThenName } from '../constants/shifts';
import CollectionFilters, { ColumnFilters, matchesCollectionFilters } from '../components/CollectionFilters';
import { BILL_TYPE_META } from '../constants/billingTypes';
import ConfirmDialog from '../components/ConfirmDialog';
import DutyTypeSelect from '../components/DutyTypeSelect';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// The duty types offered in the row dropdown, in the order the patient forms
// list them (AddPatientModal / EditPatientModal each hold their own copy).
// "Specify" is left out on purpose — there is nowhere here to type the free
// text that goes with it, so it can only be set from the patient form.
const DUTY_TYPES = [
  '08 Hrs', '12 Dual', '12 Day', '12 Night', '24 Hrs',
  'Nursing Visit', 'Physiotherapy', 'Medical Equipment', 'Consumables',
];

interface MonthlyBill {
  id: number;
  bill_no: number | null;
  invoice_no?: string | null;
  bill_date: string;
  invoice_period_start: string;
  invoice_period_end: string;
  total_amount: number;
  amount_received: number;
  status: string;
  bill_type: string | null;
  patient_id: number;
  patient_name: string;
  client_name: string | null;
  /** Every client on the case — a patient can have more than one. */
  client_names?: string[];
  contact: string | null;
  receipt_pdf_url: string | null;
  has_payment_receipt: boolean;
  is_carryover: boolean;
  duty_type: string | null;
  case_manager: string | null;
  expected_payment_date: string | null;
  /** The team's internal chase note. NOT the invoice `remarks` field. */
  collection_remarks: string | null;
}

interface OverrideRow {
  patient_id: number;
  month: string;
  included: boolean;
  /** The team's reference number for the case; null until one is assigned. */
  sl_no: number | null;
}

interface PatientLite {
  id: number;
  patient_name: string;
  case_start_date: string;
  duty_type?: string | null;
  patient_clients: { client_name?: string; name?: string }[];
}

function fmt(dateStr: string | null | undefined) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtAmount(n: number) {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function monthLabel(ym: string) {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function prevMonth(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonth(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const statusStyle = (status: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string }> = {
    Pending:   { bg: '#fef3c7', color: '#92400e' },
    Collected: { bg: '#dcfce7', color: '#15803d' },
    Processed: { bg: '#dbeafe', color: '#1e40af' },
    // Consumable returns: a credit note, adjusted rather than collected.
    Completed: { bg: '#f1f5f9', color: '#334155' },
  };
  const s = map[status] || { bg: '#f1f5f9', color: '#475569' };
  return {
    display: 'inline-block', padding: '3px 10px', borderRadius: '9999px',
    fontSize: '0.78rem', fontWeight: 600, backgroundColor: s.bg, color: s.color,
  };
};

const clientNameOf = (p: PatientLite) => {
  const c = [...(p.patient_clients || [])].sort((a: any, b: any) => (a.id || 0) - (b.id || 0))[0];
  return (c?.client_name || c?.name || '').trim();
};

// How a bill is named in toasts and the remove dialog — its invoice number when
// it has one, otherwise the bill number the table shows.
const billLabel = (b: MonthlyBill) =>
  b.bill_type === 'consumable_return'
    ? (b.invoice_no || `R-${b.bill_no}`)
    : (b.invoice_no || `#${b.bill_no}`);

// ── Add-picker styles, shared by both of its sections ───────────────────────
// Sized to be read at a glance rather than squinted at — this is a list the
// team scans down looking for a name, not a dense data table.
const pickerHeading: React.CSSProperties = {
  padding: '10px 20px 6px', fontSize: '11px', fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
};

const pickerRow = (ticked: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer',
  padding: '13px 20px', borderBottom: '1px solid #f1f5f9',
  background: ticked ? '#eff6ff' : 'transparent',
});

const pickerCheckbox: React.CSSProperties = {
  cursor: 'pointer', accentColor: '#2563eb', flexShrink: 0,
  width: '17px', height: '17px',
};

const pickerTitle: React.CSSProperties = {
  fontSize: '15px', fontWeight: 700, color: '#0f172a',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

const pickerSub: React.CSSProperties = {
  fontSize: '12.5px', color: '#64748b', marginTop: '3px',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

const pickerCancelBtn: React.CSSProperties = {
  padding: '8px 18px', borderRadius: '8px', border: '1px solid #cbd5e1',
  background: '#fff', color: '#475569', fontSize: '13px', fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit',
};

const pickerAddBtn: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '8px', border: 'none', color: '#fff',
  fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
};

/**
 * The order this page reads in: duty type, then client name A→Z.
 *
 * Pending and collected rows are deliberately mixed. They used to be split,
 * with everything settled pushed to the bottom — but that is what made a
 * position-derived number impossible, because collecting one bill renumbered
 * everything below it. One continuous order is what lets SL be derived.
 *
 * The bill-no tie-break only matters for two bills of one client and duty,
 * where it stops them swapping places between renders.
 */
const compareCollectionOrder = (a: MonthlyBill, b: MonthlyBill) =>
  compareByDutyThenName(
    { duty_type: a.duty_type, name: clientLabel(a) },
    { duty_type: b.duty_type, name: clientLabel(b) },
  ) || (a.bill_no ?? Infinity) - (b.bill_no ?? Infinity);

// Pending used to be defined here as "the remainder after a part payment", and
// showed nothing at all on a bill nobody had paid against — so the column was
// blank on exactly the bills the team most needed to chase. It now means what
// it says: everything still owed. Shared with the totals via outstandingOf.

const CurrentPaymentCollections: React.FC = () => {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(currentYearMonth());
  const [bills, setBills] = useState<MonthlyBill[]>([]);
  const [patients, setPatients] = useState<PatientLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // A filter set against one month means nothing in the next — a case manager
  // or expected-payment range from August would silently empty September.
  useEffect(() => {
    setColumnFilters({});
    setDateFrom('');
    setDateTo('');
  }, [selectedMonth]);

  // Cases on this month's list, and the month each decision was made in.
  const [included, setIncluded] = useState<Set<number>>(new Set());
  const [changedIn, setChangedIn] = useState<Record<number, string>>({});
  // SL is no longer state: it is derived from the sort further down. Nothing is
  // typed, nothing is stored, and nothing can drift out of step with the order
  // on screen.
  //
  // Keyed on bill id, not on the case: a case has several rows on this page,
  // and a remark belongs to one of them.
  const [editingRemarksForBill, setEditingRemarksForBill] = useState<number | null>(null);
  const [remarksDraft, setRemarksDraft] = useState('');
  // The expected-payment date shows as plain text until it is clicked, so the
  // column costs a date's width rather than a date input's.
  const [editingDateForBill, setEditingDateForBill] = useState<number | null>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  // Individual bills dropped from the list. They aren't shown on the page at
  // all — the Add picker is the one place anything comes back from.
  const [excludedBills, setExcludedBills] = useState<Set<number>>(new Set());
  // Rows ticked for removing several at once.
  const [selectedBills, setSelectedBills] = useState<Set<number>>(new Set());

  const [addingCase, setAddingCase] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  // Ticked in the Add picker: whole cases, and individual bills to put back.
  const [addCases, setAddCases] = useState<Set<number>>(new Set());
  const [addBills, setAddBills] = useState<Set<number>>(new Set());

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const monthParam = `${selectedMonth}-01`;

  const fetchBills = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/billing/monthly-bills`, {
        params: { month: selectedMonth }, headers,
      });
      setBills(res.data || []);
    } catch {
      toast.error('Failed to load bills');
    }
  }, [selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPatients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/patients/?limit=200`, { headers });
      setPatients(res.data || []);
    } catch (err) {
      console.error('Failed to fetch patients', err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Opt-in: only cases whose latest decision was "add" are on the list, so a
  // case nobody has touched — including a brand-new one — stays off.
  const fetchMembership = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/billing/current-collections`, {
        headers, params: { month: monthParam },
      });
      const rows: OverrideRow[] = res.data?.effective || [];
      setIncluded(new Set(rows.filter(r => r.included).map(r => r.patient_id)));
      setChangedIn(Object.fromEntries(rows.map(r => [r.patient_id, r.month])));
      // r.sl_no is ignored: the server still stores one from when the numbers
      // were entered by hand, but the page derives its own from the sort order.
    } catch (err) {
      console.error('Failed to fetch current collections list', err);
    }
  }, [monthParam]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Save a bill's collection remark.
   *
   * Per BILL, not per case: the note is about chasing this invoice, and a
   * client with five bills is rarely held up on all five for the same reason.
   *
   * This writes `collection_remarks`, which exists only for this page. The
   * bill's own `remarks` field is printed on the invoice the client receives,
   * so an internal note like "client disputes the night charges" must never go
   * anywhere near it.
   */
  const saveRemarks = async (bill: MonthlyBill, value: string) => {
    setEditingRemarksForBill(null);
    const previous = bill.collection_remarks;
    const next = value.trim() || null;
    if (next === previous) return;
    setBills(prev => prev.map(b =>
      b.id === bill.id ? { ...b, collection_remarks: next } : b));   // optimistic
    try {
      await axios.patch(
        `${API_BASE_URL}/api/billing/bills/${bill.id}`,
        { collection_remarks: next },
        { headers },
      );
      toast.success(next ? 'Remark saved' : 'Remark cleared');
    } catch (err) {
      console.error('Failed to save remark', err);
      setBills(prev => prev.map(b =>
        b.id === bill.id ? { ...b, collection_remarks: previous } : b));   // revert
      toast.error('Could not save the remark. Please try again.');
    }
  };

  // Open the native calendar as soon as the cell turns into an input, so one
  // click gets the team to the picker instead of two.
  //
  // showPicker() needs a recent user gesture and isn't in every browser; when
  // it isn't available or is refused, the input is still focused and typed into
  // or opened by its own control, so nothing is lost.
  useEffect(() => {
    if (editingDateForBill === null) return;
    const el = dateInputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (el && typeof el.showPicker === 'function') {
      try { el.showPicker(); } catch { /* falls back to the focused input */ }
    }
  }, [editingDateForBill]);

  /** Set a bill's expected payment date. Blank clears it. */
  const saveExpectedDate = async (bill: MonthlyBill, value: string) => {
    const previous = bill.expected_payment_date;
    const next = value || null;
    if (next === previous) return;
    setBills(prev => prev.map(b =>
      b.id === bill.id ? { ...b, expected_payment_date: next } : b));   // optimistic
    try {
      await axios.patch(
        `${API_BASE_URL}/api/billing/bills/${bill.id}`,
        { expected_payment_date: next },
        { headers },
      );
      toast.success(next ? `Expected ${fmt(next)}` : 'Expected date cleared');
    } catch (err) {
      console.error('Failed to set expected payment date', err);
      setBills(prev => prev.map(b =>
        b.id === bill.id ? { ...b, expected_payment_date: previous } : b));   // revert
      toast.error('Could not save the date. Please try again.');
    }
  };

  /**
   * Set a case's case manager.
   *
   * This lives on the PATIENT, not the bill — so it changes for every bill of
   * that case and everywhere else the case manager is shown, not just this
   * month's row. The confirmation says which client it applied to for that
   * reason.
   */
  const saveCaseManager = async (bill: MonthlyBill, value: string) => {
    const previous = bill.case_manager;
    const next = value || null;
    if (next === previous) return;
    const pid = bill.patient_id;
    setBills(prev => prev.map(b =>
      b.patient_id === pid ? { ...b, case_manager: next } : b));   // optimistic, whole case
    try {
      // The patient endpoint takes multipart with a JSON blob, and applies it
      // with exclude_unset — so one field alone is a safe partial update.
      const form = new FormData();
      form.append('patient_data', JSON.stringify({ case_manager: next }));
      await axios.patch(`${API_BASE_URL}/api/patients/${pid}`, form, { headers });
      toast.success(`${clientOfPatient(pid)} → ${next || 'no case manager'}`);
    } catch (err) {
      console.error('Failed to set case manager', err);
      setBills(prev => prev.map(b =>
        b.patient_id === pid ? { ...b, case_manager: previous } : b));   // revert
      toast.error('Could not save the case manager. Please try again.');
    }
  };

  /** Case managers already in use, for the row dropdown. */
  const caseManagerOptions = Array.from(
    new Set(bills.map(b => (b.case_manager || '').trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b));

  /**
   * Set a case's duty type.
   *
   * Like the case manager, this belongs to the PATIENT — so it changes on the
   * ops board, the client lists and everywhere else, not only this row. The
   * backend also updates the case's primary duty schedule, without which the
   * next save of the patient form would put the old duty type back.
   *
   * Changing it re-sorts the list, because duty type is the second sort key.
   */
  const saveDutyType = async (bill: MonthlyBill, value: string) => {
    const previous = bill.duty_type;
    const next = value || null;
    if (next === previous) return;
    const pid = bill.patient_id;
    setBills(prev => prev.map(b =>
      b.patient_id === pid ? { ...b, duty_type: next } : b));   // optimistic, whole case
    try {
      const form = new FormData();
      // duty_type_specify goes with it: it only shows when the duty type is
      // "Specify"/"Other", and leaving a stale one behind is just litter.
      form.append('patient_data', JSON.stringify({ duty_type: next, duty_type_specify: '' }));
      await axios.patch(`${API_BASE_URL}/api/patients/${pid}`, form, { headers });
      toast.success(`${clientOfPatient(pid)} → ${next || 'no duty type'}`);
    } catch (err) {
      console.error('Failed to set duty type', err);
      setBills(prev => prev.map(b =>
        b.patient_id === pid ? { ...b, duty_type: previous } : b));   // revert
      toast.error('Could not save the duty type. Please try again.');
    }
  };

  /**
   * The duty types offered in the row dropdown: the standard list, plus
   * anything already on a case that isn't in it. Without that tail a case
   * carrying a legacy or hand-typed duty type would show blank in the select,
   * and the first stray click would overwrite it.
   */
  const dutyTypeOptions = Array.from(new Set([
    ...DUTY_TYPES,
    ...bills.map(b => (b.duty_type || '').trim()).filter(Boolean),
  ]));

  /** The name shown in the Client column for a case, for use in messages. */
  const clientOfPatient = (patientId: number) => {
    const b = bills.find(x => x.patient_id === patientId);
    return b ? (b.client_name || b.patient_name) : `LHHS${patientId}`;
  };

  /** Drop or restore many bills in one request. */
  const setBillsExcluded = async (billIds: number[], excluded: boolean) => {
    if (billIds.length === 0) return;
    const before = new Set(excludedBills);
    setExcludedBills(prev => {                    // optimistic
      const n = new Set(prev);
      billIds.forEach(id => (excluded ? n.add(id) : n.delete(id)));
      return n;
    });
    setSelectedBills(new Set());
    try {
      await axios.post(
        `${API_BASE_URL}/api/billing/current-collections/excluded-bills/bulk`,
        { bill_ids: billIds, excluded },
        { headers },
      );
      const n = billIds.length;
      toast.success(excluded
        ? `${n} bill${n === 1 ? '' : 's'} removed`
        : `${n} bill${n === 1 ? '' : 's'} put back`);
      // Removing a client's last visible bill leaves the case on the list
      // showing nothing — and invisible in Add Case, so there's no way back.
      // Take it off instead, which is how the team expects to recover it.
      if (excluded) await dropEmptiedCases(billIds);
    } catch (err) {
      console.error('Failed to update removed bills', err);
      setExcludedBills(before);                   // revert
      toast.error('Could not update the list. Please try again.');
    }
  };

  /** Take off the list any case whose every bill has now been removed. */
  const dropEmptiedCases = async (justRemoved: number[]) => {
    const gone = new Set([...Array.from(excludedBills), ...justRemoved]);
    const affected = new Set(
      bills.filter(b => justRemoved.includes(b.id)).map(b => b.patient_id));
    for (const pid of Array.from(affected)) {
      const stillShowing = bills.some(b => b.patient_id === pid && !gone.has(b.id));
      if (!stillShowing && included.has(pid)) {
        await setMembership(pid, clientOfPatient(pid), false);
      }
    }
  };

  // Individual bills dropped from the list. Not month-scoped — a bill the team
  // isn't chasing stays off when it carries into later months.
  const fetchExcludedBills = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/billing/current-collections/excluded-bills`, { headers });
      setExcludedBills(new Set<number>(res.data || []));
    } catch (err) {
      console.error('Failed to fetch removed bills', err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchBills(), fetchPatients(), fetchMembership(), fetchExcludedBills()])
      .finally(() => setLoading(false));
  }, [fetchBills, fetchPatients, fetchMembership, fetchExcludedBills]);

  // Drop one bill, or put it back. The case stays on the list either way, so the
  // client's other bills are untouched and a new bill for them still appears.
  const setBillExcluded = async (bill: MonthlyBill, excluded: boolean) => {
    const who = bill.client_name || bill.patient_name;
    setExcludedBills(prev => {          // optimistic
      const n = new Set(prev);
      excluded ? n.add(bill.id) : n.delete(bill.id);
      return n;
    });
    try {
      await axios.post(
        `${API_BASE_URL}/api/billing/current-collections/excluded-bills`,
        { bill_id: bill.id, excluded },
        { headers },
      );
      toast.success(excluded ? `${billLabel(bill)} removed` : `${billLabel(bill)} restored for ${who}`);
    } catch (err) {
      console.error('Failed to update removed bills', err);
      setExcludedBills(prev => {        // revert
        const n = new Set(prev);
        excluded ? n.delete(bill.id) : n.add(bill.id);
        return n;
      });
      toast.error('Could not update the list. Please try again.');
    }
  };

  // Add / remove for the selected month ONWARDS. Earlier months keep what they
  // had; later months follow until the team makes another change there.
  const setMembership = async (patientId: number, who: string, onList: boolean) => {
    const wasOnList = included.has(patientId);
    const prevChangedIn = changedIn[patientId];

    setIncluded(prev => {
      const n = new Set(prev);
      onList ? n.add(patientId) : n.delete(patientId);
      return n;
    });
    setChangedIn(prev => ({ ...prev, [patientId]: monthParam }));

    try {
      // The response carries an sl_no, deliberately unread: the number comes
      // from where the case lands in the sort, which the reload below settles.
      // Naming one here would be a guess the list then contradicts.
      await axios.post(
        `${API_BASE_URL}/api/billing/current-collections/override`,
        { patient_id: patientId, month: monthParam, included: onList },
        { headers },
      );
      toast.success(
        onList
          ? `${who} added from ${monthLabel(selectedMonth)} onwards`
          : `${who} removed from ${monthLabel(selectedMonth)} onwards`,
      );
    } catch (err) {
      console.error('Failed to update collections list', err);
      setIncluded(prev => {          // revert
        const n = new Set(prev);
        wasOnList ? n.add(patientId) : n.delete(patientId);
        return n;
      });
      setChangedIn(prev => {
        const n = { ...prev };
        if (prevChangedIn === undefined) delete n[patientId]; else n[patientId] = prevChangedIn;
        return n;
      });
      toast.error('Could not update the list. Please try again.');
    }
  };

  // Every bill belonging to a case on the list, before per-bill removals.
  const caseBills = bills.filter(b => included.has(b.patient_id));
  const onListBills = caseBills.filter(b => !excludedBills.has(b.id));

  /**
   * The SL number for each case, derived from the list rather than stored.
   *
   * Walk every bill in the canonical order and hand the next number to each
   * CLIENT as they first appear. One number per client, so both a client's
   * several bills and their several cases carry the same one — chasing number 3
   * means one phone call, whoever it is about.
   *
   * Because the sort is by duty type first, a client covering two patients on
   * different duty types appears in two places, and their number repeats out of
   * sequence lower down (1, 2, 3, 4, 2). That is deliberate: keeping the duty
   * sections intact was worth more than a strictly ascending column.
   *
   * "Same client" can only mean the same name — there is no client record in
   * this system, just free text on each patient — matched on the first client
   * name, which is also what the sort reads. Two unrelated clients who happen
   * to share a name would share a number; nothing here can tell them apart.
   *
   * Computed over the WHOLE list, never over the filtered rows — otherwise a
   * search or a status filter would renumber every case that survived it, and
   * the number would mean something different on every screen.
   */
  const slNos: Record<number, number> = (() => {
    const byClient: Record<string, number> = {};
    const map: Record<number, number> = {};
    let next = 0;
    [...onListBills].sort(compareCollectionOrder).forEach((b) => {
      if (b.patient_id in map) return;
      const key = clientLabel(b).toLowerCase();
      // A blank name is not a client they have in common — never let two
      // nameless cases collapse into one number.
      if (!key) { map[b.patient_id] = ++next; return; }
      if (!(key in byClient)) byClient[key] = ++next;
      map[b.patient_id] = byClient[key];
    });
    return map;
  })();
  // Removed bills of cases still on the list — the ones the "N removed" toggle
  // reveals so they can be restored. A removed bill whose case was taken off
  // entirely isn't shown, since the case is gone from the page anyway.
  const removedBills = caseBills.filter(b => excludedBills.has(b.id));

  const [pendingRemoval, setPendingRemoval] = useState<MonthlyBill | null>(null);
  const removalWho = pendingRemoval
    ? (pendingRemoval.client_name || pendingRemoval.patient_name)
    : '';

  const filtered = onListBills
    .filter((b) => {
      const term = search.toLowerCase();
      const matchSearch =
        !term ||
        b.patient_name.toLowerCase().includes(term) ||
        // Any client on the case, not only the first — otherwise searching for
        // the second name on a two-client case finds nothing.
        (b.client_names && b.client_names.length > 0
          ? b.client_names.some(n => n.toLowerCase().includes(term))
          : (b.client_name || '').toLowerCase().includes(term)) ||
        (b.duty_type || '').toLowerCase().includes(term) ||
        `#${b.bill_no}`.includes(term) ||
        (b.invoice_no || '').toLowerCase().includes(term) ||
        `LHHS${b.patient_id}`.toLowerCase().includes(term) ||
        // The SL number, matched exactly rather than as a substring — typing 1
        // should find case 1, not 1, 10, 11 and 21. Guarded against a blank
        // term, which would otherwise equal the empty string a case with no
        // number renders as, and match exactly the unnumbered rows.
        (term.trim() !== '' && String(slNos[b.patient_id] ?? '') === term.trim());

      return matchSearch && matchesCollectionFilters(b, columnFilters, dateFrom, dateTo);
    })
    // The one order the page has. Sorting by SL was a separate option while the
    // numbers were entered by hand and could disagree with the reading order;
    // now SL is derived from this very sort, so the two can't differ.
    .sort(compareCollectionOrder);

  // Cases not on this month's list, newest first — the reason to open the
  // picker is almost always a case that has just started billing.
  const offList = patients
    .filter(p => !included.has(p.id))
    .filter(p => {
      const q = addSearch.trim().toLowerCase();
      if (!q) return true;
      return p.patient_name.toLowerCase().includes(q)
        || clientNameOf(p).toLowerCase().includes(q)
        || `LHHS${p.id}`.toLowerCase().includes(q);
    })
    .sort((a, b) => (b.case_start_date || '').localeCompare(a.case_start_date || ''));

  // Bills removed one at a time, offered back in the same picker. Only those of
  // cases still on the list — if the whole case came off, it appears above as a
  // case instead, and adding it restores its bills anyway.
  const addBackBills = removedBills
    .filter(b => {
      const q = addSearch.trim().toLowerCase();
      if (!q) return true;
      return b.patient_name.toLowerCase().includes(q)
        || (b.client_name || '').toLowerCase().includes(q)
        || billLabel(b).toLowerCase().includes(q)
        || `LHHS${b.patient_id}`.toLowerCase().includes(q);
    })
    .sort(compareCollectionOrder);

  const closeAdd = () => {
    setAddingCase(false);
    setAddSearch('');
    setAddCases(new Set());
    setAddBills(new Set());
  };

  /** Put everything ticked onto the list in one go. */
  const addSelected = async () => {
    const caseIds = Array.from(addCases);
    const billIds = Array.from(addBills);
    closeAdd();
    // Bills first: adding a case clears its removals server-side, so doing it
    // the other way round would just undo itself.
    if (billIds.length) await setBillsExcluded(billIds, false);
    for (const pid of caseIds) {
      await setMembership(pid, clientOfPatient(pid), true);
    }
    // The month's bills are loaded once, so a bill raised since then isn't in
    // state — adding its case would put the row on the list with nothing to
    // show until the page was reloaded by hand. Re-read them instead.
    if (caseIds.length) await fetchBills();
  };

  // Rendered above AND below the table. A long month means ticking rows near
  // the bottom and then scrolling all the way back up to act on them.
  const bulkBar = (where: 'top' | 'bottom') => selectedBills.size === 0 ? null : (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      marginBottom: where === 'top' ? '0.75rem' : 0,
      marginTop: where === 'bottom' ? '0.75rem' : 0,
      padding: '0.6rem 1rem', backgroundColor: '#eff6ff',
      border: '1px solid #bfdbfe', borderRadius: '10px',
    }}>
      <span style={{ fontSize: '0.86rem', fontWeight: 600, color: '#1d4ed8' }}>
        {selectedBills.size} selected
      </span>
      <button
        onClick={() => setBillsExcluded(Array.from(selectedBills), true)}
        style={{
          padding: '0.35rem 0.9rem', border: 'none', borderRadius: '7px',
          cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#fff',
          background: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)',
        }}
      >
        Remove selected
      </button>
      <button
        onClick={() => setSelectedBills(new Set())}
        style={{
          padding: '0.35rem 0.9rem', border: '1px solid #cbd5e1', borderRadius: '7px',
          background: '#fff', cursor: 'pointer', fontSize: '0.82rem',
          fontWeight: 600, color: '#475569',
        }}
      >
        Clear selection
      </button>
    </div>
  );

  const casesOnList = new Set(onListBills.map(b => b.patient_id)).size;
  // Money is summed per bill, not per bucket.
  //
  // These used to put a bill's WHOLE amount in one bucket or the other, chosen
  // by its status — so a ₹55,000 bill with ₹47,500 already received reported
  // ₹55,000 pending and nothing collected, overstating what was owed by the
  // ₹47,500 already in hand and understating collections by the same. The
  // totals still added up to the billed figure, which is why it went unnoticed.
  //
  // Now each bill contributes its own billed / collected / pending, exactly the
  // three figures its row shows, so the subtotals are the columns added up and
  // billed = collected + pending always holds.
  const totalAmount = filtered.reduce((s, b) => s + b.total_amount, 0);
  const totalCollected = filtered.reduce((s, b) => s + collectedOf(b), 0);
  const totalPending = filtered.reduce((s, b) => s + outstandingOf(b), 0);
  // Counts stay per bill, off the derived status, so they match the table and
  // the filter buttons.
  const pendingCount = filtered.filter((b) => !isCollected(b)).length;
  const collectedCount = filtered.filter((b) => isCollected(b)).length;

  const isCurrentMonth = selectedMonth === currentYearMonth();

  // borderRight rules each column off, so a wide row can be followed across
  // without losing which value belongs to which heading.
  // The arrows either side of the month, borderless inside their shared box.
  const stepBtn: React.CSSProperties = {
    padding: '7px 12px', border: 'none', background: 'transparent',
    cursor: 'pointer', fontSize: '0.92rem', color: '#475569', lineHeight: 1,
    fontFamily: 'inherit',
  };

  // The blue header band from the nurse list, so the two tables read as one
  // system. Note it is navy-on-blue there, not white-on-blue: white on this
  // blue lands around 2:1 contrast, and these headings are small and uppercase.
  const th: React.CSSProperties = {
    padding: '0.6rem 0.5rem', textAlign: 'center', fontSize: '0.66rem',
    fontWeight: 700, color: '#1a3a5c', textTransform: 'uppercase',
    letterSpacing: '0.03em', backgroundColor: '#86afd8',
    // Rules in a lighter blue rather than the body's grey, which read as dirt
    // against the band.
    borderBottom: '1px solid #6f9bc9', borderRight: '1px solid #9dbfe1',
    // Headings wrap onto a second line rather than setting the column's width.
    // "Exp. Pay Date" over two lines is what lets that column be narrow.
    overflowWrap: 'anywhere', lineHeight: 1.25,
  };

  return (
    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Page header, with the month stepper and Add Case on the same line.
          These had a full-width card to themselves, which cost the best part of
          a hundred pixels above every screenful of bills for three controls and
          a line of counts. */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem',
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' }}>
            Current Payment Collections
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
            The collections being chased this month. Nothing lands here on its own — add the cases you're following up.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {/* One stepper rather than two buttons pinned to opposite ends of a
                card: the month reads between its own arrows, in a fraction of
                the width. */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', overflow: 'hidden',
              border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff',
            }}>
              <button
                onClick={() => setSelectedMonth(prevMonth(selectedMonth))}
                title="Previous month"
                style={stepBtn}
              >
                ←
              </button>
              <span style={{
                padding: '0 12px', fontSize: '0.92rem', fontWeight: 700,
                color: '#0f172a', whiteSpace: 'nowrap',
                borderLeft: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0',
                alignSelf: 'stretch', display: 'inline-flex', alignItems: 'center',
              }}>
                {monthLabel(selectedMonth)}
              </span>
              <button
                onClick={() => setSelectedMonth(nextMonth(selectedMonth))}
                title="Next month"
                style={stepBtn}
              >
                →
              </button>
            </div>

            <button
              // Re-read bills and patients on open, so a case or bill created
              // since the page loaded is actually in the picker.
              onClick={() => {
                setAddingCase(true);
                setAddSearch('');
                fetchBills();
                fetchPatients();
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#fff', border: 'none',
                borderRadius: '8px', padding: '8px 14px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(37,99,235,0.25)', whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontSize: '15px', lineHeight: 1 }}>+</span> Add Case
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isCurrentMonth && (
              <span style={{
                fontSize: '0.72rem', backgroundColor: '#dbeafe', color: '#1e40af',
                padding: '2px 8px', borderRadius: '9999px', fontWeight: 600,
              }}>
                Current Month
              </span>
            )}
            <span style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap' }}>
              {casesOnList} case{casesOnList === 1 ? '' : 's'} on the list · {onListBills.length} bill{onListBills.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </div>

      {/* Summary, search and filters on one line.
          The team scrolls this page constantly; three stacked cards and then a
          search row of their own cost most of a screenful above every list of
          bills. Wraps on a narrow window rather than squeezing the search box. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        gap: '0.6rem 0.75rem', marginBottom: '1rem',
      }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem 1.4rem',
        padding: '0.55rem 1rem', flexShrink: 0,
        backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '10px',
      }}>
        {[
          // Same order as the columns, so the strip reads as their subtotals.
          { label: 'Billed', value: filtered.length, sub: totalAmount, dot: '#94a3b8', color: '#0f172a' },
          { label: 'Collected', value: collectedCount, sub: totalCollected, dot: '#22c55e', color: '#15803d' },
          { label: 'Pending', value: pendingCount, sub: totalPending, dot: '#f59e0b', color: '#b45309' },
        ].map((c, i) => (
          <React.Fragment key={c.label}>
            {/* Divider between entries, not before the first. */}
            {i > 0 && <span style={{ width: '1px', alignSelf: 'stretch', backgroundColor: '#e2e8f0' }} />}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '0.85rem' }}>
              <span style={{
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor: c.dot, flexShrink: 0,
              }} />
              <span style={{ color: '#64748b', fontWeight: 600 }}>{c.label}</span>
              <span style={{ fontWeight: 700, color: c.color, fontSize: '0.95rem' }}>{c.value}</span>
              <span style={{ color: '#cbd5e1' }}>·</span>
              <span style={{ fontWeight: 600, color: c.color }}>₹{fmtAmount(c.sub)}</span>
            </span>
          </React.Fragment>
        ))}
      </div>

        <input
          type="text"
          placeholder="Search client, patient, duty type, bill no, SL…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1, minWidth: '220px', padding: '9px 14px', borderRadius: '8px',
            border: '1px solid #e2e8f0', fontSize: '0.88rem', color: '#334155', outline: 'none',
            background: '#fff',
          }}
        />
        {/* Status lives in the panel with everything else — as a multi-select,
            so Pending + Processed can be asked for together.

            Options come from the curated set, not every bill in the month —
            offering a case manager whose cases aren't on this list would be a
            filter that can only ever return nothing. */}
        <CollectionFilters
          bills={onListBills}
          filteredBills={filtered}
          title="Current Payment Collections"
          slNos={slNos}
          filters={columnFilters}
          onFiltersChange={setColumnFilters}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
          year={Number(selectedMonth.split('-')[0])}
          month={Number(selectedMonth.split('-')[1])}
        />
      </div>

      {/* Bulk actions — only while something is ticked, so it stays out of the
          way the rest of the time. */}
      {bulkBar('top')}

      {/* Bills table */}
      {/* No overflowX: the fixed layout above sizes every column to fit, so the
          table never needs a horizontal scrollbar. overflow:hidden clips the
          blue header band to the card's rounded corners — without it the band
          squares them off. Safe here: the duty-type menu is a portal, and
          native select popups aren't clipped by an ancestor. */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        border: '1px solid #e2e8f0', overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
            {onListBills.length === 0
              ? `No cases on the list for ${monthLabel(selectedMonth)} yet. Use “Add Case” to start following one up.`
              : 'No bills match the current filter'}
          </div>
        ) : (
          // tableLayout:'fixed' + the colgroup below is what keeps fifteen
          // columns inside the window. Without it the browser sizes columns by
          // content, and Invoice Period and Actions take far more room than
          // they need while Remarks gets squeezed.
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            {/* Widths total 100. The narrow ones hold a short fixed value — a
                number, a pill, a date — and were only ever wide because the
                heading above them is long. Actions is the widest of the tail
                columns because its buttons carry real words. */}
            <colgroup>
              {[3, 12, 6.5, 5, 5.5, 7.5, 6.5, 6.5, 6.5, 5.5, 6.5, 8, 5, 9.5, 6.5].map((w, i) => (
                <col key={i} style={{ width: `${w}%` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {/* Two lines each, so a long heading sets the column's height
                    rather than its width. All centred — the alignment of the
                    data below is per column and set on the cells. */}
                {[
                  ['SL'], ['Client', 'Name'], ['Duty', 'Type'], ['Bill', 'No'], ['Type'],
                  ['Invoice', 'Period'],
                  ['Billed', 'Amount'], ['Collected', 'Amount'], ['Pending', 'Amount'],
                  ['Status'], ['Case', 'Manager'], ['Remarks'],
                  ['Exp. Pay', 'Date'], ['Actions'],
                ].map((lines) => (
                  <th key={lines.join(' ')} style={th}>
                    {lines.map((line) => <div key={line}>{line}</div>)}
                  </th>
                ))}
                {/* Remove and its ticks share one column, the tick to the
                    right of the button. Tick-all covers the rows currently in
                    view only, filters and search included, so it can never
                    remove something off screen. */}
                <th style={th}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                    Remove
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every(b => selectedBills.has(b.id))}
                      onChange={(e) => setSelectedBills(
                        e.target.checked ? new Set(filtered.map(b => b.id)) : new Set())}
                      style={{ cursor: 'pointer', accentColor: '#2563eb', width: '16px', height: '16px' }}
                      title="Select every row shown"
                    />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((bill) => {
                const typeMeta = bill.bill_type ? BILL_TYPE_META[bill.bill_type] : null;
                const pending = outstandingOf(bill);
                const collected = collectedOf(bill);

                // Nothing left to collect: the row's text goes green so a
                // settled line can be picked out while scanning, without having
                // to read across to the Status column.
                //
                // isCollected rather than `collected === bill.total_amount`, so
                // this is the same test the Status badge, the filters and the
                // subtotals use — a green row is exactly a row that doesn't say
                // Pending. It also carries the rounding tolerance, without which
                // a four-paise residue would leave a fully paid bill grey.
                const settled = isCollected(bill);
                // The slate hierarchy the table already had, shifted to green.
                //
                // Main text is pitched brighter than the green on the badges and
                // pills: those sit on a pale green fill that carries the colour
                // for them, whereas this is bare text on white, and the darker
                // green-700 that reads clearly inside a pill just read as black
                // at bold weight in the Client column.
                //
                // Main and secondary share a tone on a green row, unlike the
                // slate side. The band of greens that are dark enough to read on
                // white and light enough to still look green is narrow, and
                // splitting it further costs legibility for a distinction that
                // weight and size already make. The lower two stay put: any
                // lighter and they'd be harder to read than the greys they
                // replace, which is not what "greener" should cost.
                const ink = settled ? '#16a34a' : '#334155';        // main text
                const inkSoft = settled ? '#16a34a' : '#475569';    // secondary
                const inkMuted = settled ? '#22c55e' : '#94a3b8';   // sub-lines
                const inkFaint = settled ? '#86efac' : '#cbd5e1';   // placeholders

                const td: React.CSSProperties = {
                  // Tighter than it was: fifteen columns have to fit the window
                  // without a horizontal scrollbar.
                  padding: '0.6rem 0.5rem', fontSize: '0.82rem', color: ink,
                  borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9',
                  overflowWrap: 'anywhere',
                };
                return (
                  <tr
                    key={bill.id}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    {/* SL — the case's position in the list, repeated on every
                        bill of that client. Read-only: it follows the duty-type
                        and A→Z order, so it keeps itself right as cases are
                        added or their duty type changes. */}
                    <td style={{ ...td, fontWeight: 700, color: settled ? '#15803d' : '#0f172a' }}>
                      {slNos[bill.patient_id] ?? '—'}
                    </td>

                    {/* Client */}
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {/* Every client on the case, not just the first — a
                            patient often has two family members listed, and
                            showing one made the other invisible here. */}
                        <span style={{ fontWeight: 700 }}>
                          {(bill.client_names && bill.client_names.length > 0
                            ? bill.client_names
                            : [bill.client_name || bill.patient_name]).join(' & ')}
                        </span>
                        {bill.is_carryover && (
                          <span style={{
                            fontSize: '0.68rem', fontWeight: 700, padding: '1px 6px',
                            borderRadius: '9999px', backgroundColor: '#fef3c7', color: '#b45309',
                            border: '1px solid #fcd34d', whiteSpace: 'nowrap',
                          }}>
                            Carry Over
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: inkMuted, marginTop: '1px' }}>
                        LHHS{bill.patient_id} · {bill.patient_name}
                      </div>
                    </td>

                    {/* Duty Type — editable. Belongs to the case, so this
                        changes it for every bill of the client and everywhere
                        else it is shown. Kept in the pill's shape so the column
                        still reads the same as the other billing screens. */}
                    <td style={td}>
                      <DutyTypeSelect
                        value={bill.duty_type}
                        options={dutyTypeOptions}
                        onChange={(next) => saveDutyType(bill, next)}
                        title="Changes the duty type for this client everywhere"
                      />
                    </td>

                    {/* Bill No */}
                    <td style={{ ...td, fontWeight: 600 }}>
                      {bill.bill_type === 'consumable_return'
                        ? bill.invoice_no || `R-${bill.bill_no}`
                        : bill.invoice_no || `#${bill.bill_no}`}
                    </td>

                    {/* Bill Type */}
                    <td style={td}>
                      {typeMeta ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: '9999px',
                          fontSize: '0.75rem', fontWeight: 600,
                          backgroundColor: typeMeta.bg, color: typeMeta.color,
                          border: `1px solid ${typeMeta.border}`,
                        }}>
                          {typeMeta.label}
                        </span>
                      ) : '—'}
                    </td>

                    {/* Invoice Period */}
                    <td style={{ ...td, fontSize: '0.85rem', color: inkSoft }}>
                      {fmt(bill.invoice_period_start)} – {fmt(bill.invoice_period_end)}
                    </td>

                    {/* Billed / Collected / Pending — the three figures split
                        apart, because one "Amount" column plus a part-payment
                        remainder left the team working out the middle one in
                        their heads. Billed = Collected + Pending on every row. */}
                    <td style={{ ...td, fontWeight: 600, textAlign: 'center' }}>
                      ₹{fmtAmount(bill.total_amount)}
                    </td>

                    <td style={{ ...td, textAlign: 'center' }}>
                      {collected !== 0 ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 6px', borderRadius: '6px',
                          fontSize: '0.78rem', fontWeight: 700,
                          backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0',
                        }}>
                          ₹{fmtAmount(collected)}
                        </span>
                      ) : <span style={{ color: inkFaint }}>—</span>}
                    </td>

                    <td style={{ ...td, textAlign: 'center' }}>
                      {pending > 0 ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 6px', borderRadius: '6px',
                          fontSize: '0.78rem', fontWeight: 700,
                          backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
                        }}>
                          ₹{fmtAmount(pending)}
                        </span>
                      ) : <span style={{ color: inkFaint }}>—</span>}
                    </td>

                    {/* Status */}
                    <td style={td}>
                      <span style={statusStyle(effectiveStatus(bill))}>{effectiveStatus(bill)}</span>
                    </td>

                    {/* Case Manager — editable. Belongs to the case, so this
                        changes it for all of that client's bills and wherever
                        else it is shown, not only this row. */}
                    <td style={{ ...td, color: inkSoft }}>
                      <select
                        value={bill.case_manager || ''}
                        onChange={(e) => saveCaseManager(bill, e.target.value)}
                        title="Changes the case manager for this client everywhere"
                        style={{
                          width: '100%', minWidth: 0, padding: '3px 4px', borderRadius: '6px',
                          border: '1px solid #e2e8f0', background: '#fff',
                          fontSize: '0.74rem', color: ink, cursor: 'pointer',
                          fontFamily: 'inherit', boxSizing: 'border-box',
                        }}
                      >
                        <option value="">— none —</option>
                        {caseManagerOptions.map(name => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </td>

                    {/* Remarks — the team's own chase note for this bill.
                        Click to edit; Escape abandons, blur or Ctrl+Enter
                        saves. Enter alone inserts a newline, because these run
                        to more than one line often enough to matter. */}
                    <td style={td}>
                      {editingRemarksForBill === bill.id ? (
                        <textarea
                          autoFocus
                          value={remarksDraft}
                          onChange={(e) => setRemarksDraft(e.target.value)}
                          onBlur={() => saveRemarks(bill, remarksDraft)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setEditingRemarksForBill(null);
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveRemarks(bill, remarksDraft);
                          }}
                          rows={3}
                          placeholder="e.g. client says after the 15th"
                          style={{
                            width: '100%', padding: '4px 6px', borderRadius: '6px',
                            border: '1px solid #2563eb', fontSize: '0.76rem',
                            outline: 'none', fontFamily: 'inherit', resize: 'vertical',
                            boxSizing: 'border-box', color: ink,
                          }}
                        />
                      ) : (
                        <span
                          onClick={() => {
                            setEditingRemarksForBill(bill.id);
                            setRemarksDraft(bill.collection_remarks || '');
                          }}
                          title={bill.collection_remarks || 'Click to add a note'}
                          style={{
                            display: 'block', cursor: 'pointer', minHeight: '18px',
                            fontSize: '0.76rem', lineHeight: 1.35,
                            color: bill.collection_remarks ? ink : inkFaint,
                          }}
                        >
                          {bill.collection_remarks || '+ note'}
                        </span>
                      )}
                    </td>

                    {/* Expected Payment Date — the date as plain text, or a
                        calendar icon when it isn't set. Clicking either turns
                        the cell into a date input and opens the picker.

                        A date input sitting in every row was the widest thing
                        in the table for a value that is usually blank, and its
                        box and calendar button crowded a column that only ever
                        needs to show nine characters. */}
                    <td style={{ ...td, fontSize: '0.78rem', color: inkSoft }}>
                      {editingDateForBill === bill.id ? (
                        <input
                          ref={dateInputRef}
                          type="date"
                          autoFocus
                          value={(bill.expected_payment_date || '').split('T')[0]}
                          onChange={(e) => {
                            saveExpectedDate(bill, e.target.value);
                            setEditingDateForBill(null);
                          }}
                          onBlur={() => setEditingDateForBill(null)}
                          onKeyDown={(e) => { if (e.key === 'Escape') setEditingDateForBill(null); }}
                          style={{
                            // minWidth:0 lets it shrink into the column while
                            // it is open; a date input's intrinsic width would
                            // otherwise push the table past the window.
                            width: '100%', minWidth: 0, padding: '2px', borderRadius: '6px',
                            border: '1px solid #2563eb', background: '#fff',
                            fontSize: '0.7rem', color: ink,
                            fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
                          }}
                        />
                      ) : (
                        <span
                          onClick={() => setEditingDateForBill(bill.id)}
                          title={bill.expected_payment_date
                            ? 'Click to change the expected payment date'
                            : 'Click to set an expected payment date'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            cursor: 'pointer', whiteSpace: 'nowrap',
                          }}
                        >
                          {bill.expected_payment_date ? fmt(bill.expected_payment_date) : (
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none"
                              stroke={inkFaint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="5" width="18" height="16" rx="2" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                              <line x1="8" y1="3" x2="8" y2="7" />
                              <line x1="16" y1="3" x2="16" y2="7" />
                            </svg>
                          )}
                        </span>
                      )}
                    </td>

                    {/* Actions — full labels, one per line. Stacked rather than
                        side by side so each button gets the whole column width
                        and its wording is never clipped; nowrap so a label can
                        only ever break the layout visibly, not silently. */}
                    <td style={td}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <button
                          onClick={() => navigate(`/billing/patient/${bill.patient_id}`, { state: { from: '/billing/current-collections' } })}
                          style={{
                            padding: '0.28rem 0.3rem', border: 'none', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                            backgroundColor: '#dbeafe', color: '#1e40af',
                            whiteSpace: 'nowrap', width: '100%',
                          }}
                        >
                          View Bills
                        </button>
                        {bill.receipt_pdf_url ? (
                          <button
                            onClick={() => navigate(`/billing/patient/${bill.patient_id}`, { state: { from: '/billing/current-collections' } })}
                            style={{
                              padding: '0.28rem 0.3rem', border: 'none', borderRadius: '6px',
                              cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                              backgroundColor: '#f5f3ff', color: '#7c3aed',
                              whiteSpace: 'nowrap', width: '100%',
                            }}
                          >
                            Receipt Saved ✓
                          </button>
                        ) : (
                          <button
                            onClick={() => navigate(`/billing/receipt/${bill.id}`)}
                            style={{
                              padding: '0.28rem 0.3rem', border: 'none', borderRadius: '6px',
                              cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                              backgroundColor: '#f0fdf4', color: '#15803d',
                              whiteSpace: 'nowrap', width: '100%',
                            }}
                          >
                            Record Payment
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Removes this bill alone — the client's other bills stay.
                        The tick for bulk removal sits in the same cell, to the
                        right of the button, so choosing rows and acting on them
                        happen in one place. */}
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button
                          onClick={() => setPendingRemoval(bill)}
                          title="Remove this bill from the list"
                          style={{
                            padding: '0.25rem 0.45rem', border: 'none', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                            background: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)', color: 'white',
                          }}
                        >
                          Remove
                        </button>
                        <input
                          type="checkbox"
                          checked={selectedBills.has(bill.id)}
                          onChange={() => setSelectedBills(prev => {
                            const n = new Set(prev);
                            n.has(bill.id) ? n.delete(bill.id) : n.add(bill.id);
                            return n;
                          })}
                          title="Select for removing several at once"
                          style={{ cursor: 'pointer', accentColor: '#2563eb', width: '16px', height: '16px', flexShrink: 0 }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {bulkBar('bottom')}

      {/* Footer note */}
      {!loading && filtered.length > 0 && (
        <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#94a3b8', textAlign: 'right' }}>
          Showing {filtered.length} bill{filtered.length !== 1 ? 's' : ''} for {monthLabel(selectedMonth)}
          {filtered.length !== onListBills.length ? ` (filtered from ${onListBills.length})` : ''}
          {onListBills.filter(b => b.is_carryover).length > 0 && (
            <span style={{ marginLeft: '8px', color: '#b45309' }}>
              · {onListBills.filter(b => b.is_carryover).length} carried over from previous months
            </span>
          )}
        </div>
      )}

      {/* Remove confirmation. Says plainly what leaves, what stays, and that
          it's reversible — the three things the old window.confirm buried. */}
      <ConfirmDialog
        open={pendingRemoval !== null}
        danger
        title={pendingRemoval ? `Remove bill ${billLabel(pendingRemoval)}?` : ''}
        message={`This one bill of ${removalWho}'s will stop showing here.`}
        points={[
          'Their other bills stay on the list, and new bills for them will still appear.',
          'It stays off in later months too, so you won\'t have to remove it again.',
          'No bill is deleted — this only changes what this page shows.',
          'You can put it back from “removed” under the month.',
        ]}
        confirmLabel="Remove this bill"
        onConfirm={() => {
          if (pendingRemoval) setBillExcluded(pendingRemoval, true);
          setPendingRemoval(null);
        }}
        onCancel={() => setPendingRemoval(null)}
      />

      {/* The duplicate-number warning lived here. Numbers are no longer typed,
          so two cases can't be given the same one by accident. */}

      {/* Add case modal */}
      {addingCase && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 5000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)',
          }}
          onClick={() => { setAddingCase(false); setAddSearch(''); }}
        >
          <div
            style={{
              // Wide enough that a client name, bill number, duty type, period
              // and amount all read without being cut short — at 460px the
              // detail line under each row was ellipsed away.
              background: '#fff', borderRadius: '14px', width: '820px', maxWidth: '94vw', maxHeight: '86vh',
              overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                  Add to {monthLabel(selectedMonth)}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>
                  Tick anything you want on the list, then Add. Cases are numbered in the
                  order you tick them. Applies from this month onwards.
                </div>
              </div>
              <button
                onClick={closeAdd}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px', lineHeight: 1, width: '28px', height: '28px' }}
              >
                ✕
              </button>
            </div>
            <input
              autoFocus
              value={addSearch}
              onChange={e => setAddSearch(e.target.value)}
              placeholder="Search patient name or client…"
              style={{ width: '100%', padding: '11px 16px', border: 'none', borderBottom: '1px solid #e2e8f0', fontSize: '13px', outline: 'none', background: '#fafafa', fontFamily: 'inherit', color: '#0f172a', boxSizing: 'border-box' }}
            />

            {/* Grows with the dialog rather than being pinned to 340px, so a
                long list uses the height available instead of scrolling early. */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: '160px' }}>
              {offList.length === 0 && addBackBills.length === 0 ? (
                <div style={{ padding: '28px 24px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                  {addSearch.trim()
                    ? 'Nothing matches that search.'
                    : 'Everything is already on this month’s list.'}
                </div>
              ) : (
                <>
                  {/* Whole cases. Adding one brings every bill it has, including
                      any that were removed earlier. */}
                  {offList.length > 0 && (
                    <div style={pickerHeading}>Cases not on the list</div>
                  )}
                  {offList.map(p => {
                    const client = clientNameOf(p) || p.patient_name;
                    const billCount = bills.filter(b => b.patient_id === p.id).length;
                    const ticked = addCases.has(p.id);
                    return (
                      <label key={`case-${p.id}`} style={pickerRow(ticked)}>
                        <input
                          type="checkbox"
                          checked={ticked}
                          onChange={() => setAddCases(prev => {
                            const n = new Set(prev);
                            n.has(p.id) ? n.delete(p.id) : n.add(p.id);
                            return n;
                          })}
                          style={pickerCheckbox}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={pickerTitle}>{client}</div>
                          <div style={pickerSub}>
                            {p.patient_name} · LHHS{p.id}
                            {p.duty_type ? ` · ${p.duty_type}` : ''}
                            {` · ${billCount} bill${billCount === 1 ? '' : 's'} this month`}
                          </div>
                        </div>
                        {/* No SL preview any more: the number depends on where
                            the case lands in the duty-type and A→Z order, not
                            on the order things are ticked, and adding one shifts
                            the cases below it. There is nothing useful to
                            promise here before the list has been rebuilt. */}
                      </label>
                    );
                  })}

                  {/* Bills taken off individually, whose case is still on the
                      list. They live here rather than in a panel of their own so
                      there is one place to put anything back, with one word for
                      it. */}
                  {addBackBills.length > 0 && (
                    <div style={pickerHeading}>Removed bills</div>
                  )}
                  {addBackBills.map(b => {
                    const ticked = addBills.has(b.id);
                    return (
                      <label key={`bill-${b.id}`} style={pickerRow(ticked)}>
                        <input
                          type="checkbox"
                          checked={ticked}
                          onChange={() => setAddBills(prev => {
                            const n = new Set(prev);
                            n.has(b.id) ? n.delete(b.id) : n.add(b.id);
                            return n;
                          })}
                          style={pickerCheckbox}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={pickerTitle}>
                            {billLabel(b)}
                            <span style={{ fontWeight: 500, color: '#64748b' }}> · {b.client_name || b.patient_name}</span>
                          </div>
                          <div style={pickerSub}>
                            {b.duty_type || '—'} · {fmt(b.invoice_period_start)} – {fmt(b.invoice_period_end)} · ₹{fmtAmount(b.total_amount)}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </>
              )}
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '11px 16px', borderTop: '1px solid #e2e8f0', background: '#f8fafc',
            }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                {addCases.size + addBills.size} selected
              </span>
              <span style={{ flex: 1 }} />
              <button onClick={closeAdd} style={pickerCancelBtn}>Cancel</button>
              <button
                onClick={addSelected}
                disabled={addCases.size + addBills.size === 0}
                style={{
                  ...pickerAddBtn,
                  ...(addCases.size + addBills.size === 0
                    ? { background: '#cbd5e1', cursor: 'not-allowed', boxShadow: 'none' }
                    : {}),
                }}
              >
                Add selected
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentPaymentCollections;
